import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { trackersAPI, employeesAPI, projectsAPI } from "../api/client";
import { hasPermission } from "../utils/accessControl";
import { mayPerformWithRole } from "../utils/userAccess";
import { toast } from "react-toastify";

const emptyForm = {
  employeeId: "",
  employeeName: "",
  costPerHour: "",
  workingHoursPerMonth: "160",
  csLabour: "",
  csOffice: "",
  csLaptopDesktop: "0",
  csItNetworkSecurity: "",
  csProgramManagement: "",
  utsLabour: "",
  utsOffice: "",
  utsLaptopDesktop: "20",
  utsItNetworkSecurity: "",
  utsProgramManagement: "",
};

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function num(v) {
  return Number(v) || 0;
}

function costRow(row) {
  const csTotal = (Number(row.cs_labour) || 0) + (Number(row.cs_office) || 0) + (Number(row.cs_laptop_desktop) || 0) + (Number(row.cs_it_network_security) || 0) + (Number(row.cs_program_management) || 0);
  const utsTotal = (Number(row.uts_labour) || 0) + (Number(row.uts_office) || 0) + (Number(row.uts_laptop_desktop) || 0) + (Number(row.uts_it_network_security) || 0) + (Number(row.uts_program_management) || 0);
  return { csTotal, utsTotal };
}

function detailField(label, value) {
  return (
    <div className="rm-detail-card" key={label}>
      <div className="rm-detail-label">{label}</div>
      <div className="rm-detail-value">{value === null || value === undefined || value === "" ? "\u2014" : value}</div>
    </div>
  );
}

function dateValue(value) {
  return value ? String(value).slice(0, 10) : "";
}

export default function ResourceCostMasterTab() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canView = hasPermission(user?.role, "resource_cost.view");
  const canEdit = mayPerformWithRole(user?.role, "resource_cost", "edit", "resource_cost.edit");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [allocationMap, setAllocationMap] = useState({});

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [viewRow, setViewRow] = useState(null);
  const [projectDetails, setProjectDetails] = useState([]);
  const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);
  const [projectDetailsError, setProjectDetailsError] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const empDropdownRef = useRef(null);

  const loadData = async () => {
    setError("");
    setLoading(true);
    try {
      const [costRes, allocRes] = await Promise.all([
        trackersAPI.getResourceCostEntries(),
        projectsAPI.getResourceAllocation(),
      ]);
      setData(Array.isArray(costRes.data) ? costRes.data : []);
      const map = {};
      (Array.isArray(allocRes.data) ? allocRes.data : []).forEach((a) => {
        if (!a.employee_id) return;
        map[a.employee_id] = {
          projectCount: Number(a.project_count) || 0,
          totalAllocated: Number(a.total_allocated) || 0,
        };
      });
      setAllocationMap(map);
    } catch (err) {
      console.error("Failed to load resource cost data", err);
      setError("Unable to load resource cost records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (empDropdownRef.current && !empDropdownRef.current.contains(e.target)) {
        setShowEmpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadEmployeeOptions = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 1) {
      setEmployeeOptions([]);
      return;
    }
    try {
      const res = await employeesAPI.getAll({ search: searchTerm, status: "Active", limit: 15 });
      const list = Array.isArray(res.data?.employees) ? res.data.employees : (Array.isArray(res.data) ? res.data : []);
      setEmployeeOptions(list.slice(0, 15));
    } catch {
      setEmployeeOptions([]);
    }
  }, []);

  useEffect(() => {
    if (!showEmpDropdown) return;
    const timer = setTimeout(() => { loadEmployeeOptions(empSearch); }, 300);
    return () => clearTimeout(timer);
  }, [empSearch, showEmpDropdown, loadEmployeeOptions]);

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(
      (r) =>
        (r.employee_id || "").toLowerCase().includes(q) ||
        (r.employeeName || "").toLowerCase().includes(q) ||
        (r.employee_name || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = filteredData.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setCurrentPage(1); }, [search]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const totals = useMemo(() => {
    let csLabour = 0, csOffice = 0, csLaptop = 0, csIt = 0, csPm = 0, csTotal = 0;
    let utsLabour = 0, utsOffice = 0, utsLaptop = 0, utsIt = 0, utsPm = 0, utsTotal = 0;
    let defaultHours = 160;
    for (const r of filteredData) {
      const wh = Number(r.workingHoursPerMonth) || 160;
      defaultHours = wh;
      const csRowTotal = (Number(r.cs_labour) || 0) + (Number(r.cs_office) || 0) + (Number(r.cs_laptop_desktop) || 0) + (Number(r.cs_it_network_security) || 0) + (Number(r.cs_program_management) || 0);
      const utsRowTotal = (Number(r.uts_labour) || 0) + (Number(r.uts_office) || 0) + (Number(r.uts_laptop_desktop) || 0) + (Number(r.uts_it_network_security) || 0) + (Number(r.uts_program_management) || 0);
      csLabour += Number(r.cs_labour) || 0;
      csOffice += Number(r.cs_office) || 0;
      csLaptop += Number(r.cs_laptop_desktop) || 0;
      csIt += Number(r.cs_it_network_security) || 0;
      csPm += Number(r.cs_program_management) || 0;
      csTotal += csRowTotal;
      utsLabour += Number(r.uts_labour) || 0;
      utsOffice += Number(r.uts_office) || 0;
      utsLaptop += Number(r.uts_laptop_desktop) || 0;
      utsIt += Number(r.uts_it_network_security) || 0;
      utsPm += Number(r.uts_program_management) || 0;
      utsTotal += utsRowTotal;
    }
    return {
      csLabour, csOffice, csLaptop, csIt, csPm, csTotal,
      csMonthly: csTotal * defaultHours,
      utsLabour, utsOffice, utsLaptop, utsIt, utsPm, utsTotal,
      utsMonthly: utsTotal * defaultHours,
    };
  }, [filteredData]);

  const selectEmployee = (emp) => {
    setFormData((prev) => ({
      ...prev,
      employeeId: emp.employee_id || "",
      employeeName: emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
    }));
    setEmpSearch("");
    setShowEmpDropdown(false);
    setEmployeeOptions([]);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAdd = () => {
    if (!canEdit) return;
    setEditingId(null);
    setFormData({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setEditingId(row.id || row.employee_id);
    setFormData({
      employeeId: row.employee_id || "",
      employeeName: row.employee_name || "",
      costPerHour: String(row.cost_per_hour ?? row.costPerHour ?? ""),
      workingHoursPerMonth: String(row.workingHoursPerMonth ?? row.working_hours_per_month ?? 160),
      csLabour: String(row.cs_labour ?? 0),
      csOffice: String(row.cs_office ?? 0),
      csLaptopDesktop: String(row.cs_laptop_desktop ?? 0),
      csItNetworkSecurity: String(row.cs_it_network_security ?? 0),
      csProgramManagement: String(row.cs_program_management ?? 0),
      utsLabour: String(row.uts_labour ?? 0),
      utsOffice: String(row.uts_office ?? 0),
      utsLaptopDesktop: String(row.uts_laptop_desktop ?? 20),
      utsItNetworkSecurity: String(row.uts_it_network_security ?? 0),
      utsProgramManagement: String(row.uts_program_management ?? 0),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ ...emptyForm });
  };

  const csFormTotal = num(formData.csLabour) + num(formData.csOffice) + num(formData.csLaptopDesktop) + num(formData.csItNetworkSecurity) + num(formData.csProgramManagement);
  const utsFormTotal = num(formData.utsLabour) + num(formData.utsOffice) + num(formData.utsLaptopDesktop) + num(formData.utsItNetworkSecurity) + num(formData.utsProgramManagement);
  const workingHours = num(formData.workingHoursPerMonth) || 160;
  const csFormMonthly = csFormTotal * workingHours;
  const utsFormMonthly = utsFormTotal * workingHours;

  const handleSave = async () => {
    if (!formData.employeeId.trim()) { toast.error("Employee ID is required"); return; }
    if (!formData.employeeName.trim()) { toast.error("Employee Name is required"); return; }

    const payload = {
      employeeId: formData.employeeId.trim(),
      employeeName: formData.employeeName.trim(),
      costPerHour: Number(formData.costPerHour) || 0,
      workingHoursPerMonth: Number(formData.workingHoursPerMonth) || 160,
      csLabour: Number(formData.csLabour) || 0,
      csOffice: Number(formData.csOffice) || 0,
      csLaptopDesktop: 0,
      csItNetworkSecurity: Number(formData.csItNetworkSecurity) || 0,
      csProgramManagement: Number(formData.csProgramManagement) || 0,
      utsLabour: Number(formData.utsLabour) || 0,
      utsOffice: Number(formData.utsOffice) || 0,
      utsLaptopDesktop: 20,
      utsItNetworkSecurity: Number(formData.utsItNetworkSecurity) || 0,
      utsProgramManagement: Number(formData.utsProgramManagement) || 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await trackersAPI.updateResourceCostEntry(editingId, payload);
        toast.success("Resource cost updated");
      } else {
        await trackersAPI.createResourceCostEntry(payload);
        toast.success("Resource cost created");
      }
      closeForm();
      loadData();
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (row) => {
    setDeletingId(row.id || row.employee_id);
    setDeletingRow(row);
    setShowDeleteModal(true);
  };

  const openDetails = async (row) => {
    setViewRow(row);
    setProjectDetails([]);
    setProjectDetailsError("");
    setProjectDetailsLoading(true);
    try {
      const response = await projectsAPI.getEmployeeProjectDetails(row.employee_id);
      setProjectDetails(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to load resource project details", err);
      setProjectDetailsError("Project-based details could not be loaded.");
    } finally {
      setProjectDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setViewRow(null);
    setProjectDetails([]);
    setProjectDetailsError("");
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await trackersAPI.deleteResourceCostEntry(deletingId);
      toast.success("Resource cost deleted");
      setShowDeleteModal(false);
      setDeletingId(null);
      setDeletingRow(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const canViewSection = canView || canEdit;

  if (!canViewSection) {
    return (
      <div>
        <style>{css}</style>
        <div className="rm-empty" style={{ minHeight: 300 }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
          <p style={{ margin: "8px 0 0", color: "#94A3B8", fontSize: "13px" }}>You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{css}</style>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#1E293B" }}>Resource Master</h2>
          <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#6B7280" }}>{filteredData.length} of {data.length} records</p>
        </div>
        {canEdit && (
          <button className="rm-btn rm-btn-primary" onClick={openAdd}>+ Add Resource</button>
        )}
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "14px", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "0 1 280px" }}>
          <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="rm-input rm-search"
            placeholder="Search employee name or number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rm-list-card">
        {loading ? (
          <div className="rm-empty">Loading resource data...</div>
        ) : error ? (
          <div className="rm-empty" style={{ color: "#DC2626" }}>{error}</div>
        ) : filteredData.length === 0 ? (
          <div className="rm-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>
            <p style={{ margin: "8px 0 0", color: "#94A3B8", fontSize: "13px" }}>No resource records found</p>
          </div>
        ) : (
          <div className="rm-table-wrapper">
            <table className="rm-table">
              <colgroup>
                <col className="rm-col-employee-id" />
                <col className="rm-col-employee-name" />
                <col className="rm-col-cost" />
                <col className="rm-col-cost" />
                <col className="rm-col-allocation" />
                <col className="rm-col-allocation" />
                <col className="rm-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" rowSpan={2}>Employee No.</th>
                  <th scope="col" rowSpan={2}>Employee Name</th>
                  <th scope="col" className="rm-group-header rm-group-cost" colSpan={2}>Cost</th>
                  <th scope="col" className="rm-group-header rm-group-alloc" colSpan={2}>Allocation</th>
                  <th scope="col" rowSpan={2} className="rm-actions-header">Actions</th>
                </tr>
                <tr>
                  <th scope="col" className="rm-sub-cost rm-num">Customer Site</th>
                  <th scope="col" className="rm-sub-uts rm-num">UTS Site</th>
                  <th scope="col" className="rm-sub-alloc">Projects Assigned</th>
                  <th scope="col" className="rm-sub-alloc">Total Project Allocation</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => {
                  const { csTotal, utsTotal } = costRow(row);
                  const alloc = allocationMap[row.employee_id] || { projectCount: 0, totalAllocated: 0 };
                  return (
                    <tr key={row.id || row.employee_id}>
                      <td className="rm-employee-id"><span className="rm-id-badge">{row.employee_id}</span></td>
                      <td>
                        <div className="rm-name-cell">
                          <span className="rm-avatar">{(row.employee_name || "?").charAt(0).toUpperCase()}</span>
                          {row.employee_name}
                        </div>
                      </td>
                      <td className="rm-num rm-cost-value">{fmt(csTotal)}</td>
                      <td className="rm-num">{fmt(utsTotal)}</td>
                      <td className="rm-alloc-cell">{alloc.projectCount} {alloc.projectCount === 1 ? "Project" : "Projects"}</td>
                      <td className="rm-alloc-cell rm-alloc-allocated">{alloc.totalAllocated}%</td>
                      <td className="rm-actions-cell">
                        <div className="rm-action-set">
                          <button className="rm-action-btn rm-action-view" onClick={() => openDetails(row)} title="View details">View</button>
                          {canEdit && (
                            <button className="rm-action-btn rm-action-edit" onClick={() => openEdit(row)} title="Edit">Edit</button>
                          )}
                          {canEdit && (
                            <button className="rm-action-btn rm-action-danger" onClick={() => confirmDelete(row)} title="Remove">Remove</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="rm-footer-row">
                  <td colSpan={2} style={{ fontWeight: 700, fontSize: "12px" }}>TOTAL</td>
                  <td className="rm-num" style={{ fontWeight: 700 }}>{fmt(totals.csTotal)}</td>
                  <td className="rm-num" style={{ fontWeight: 700 }}>{fmt(totals.utsTotal)}</td>
                  <td className="rm-alloc-cell">—</td>
                  <td className="rm-alloc-cell">—</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {!loading && filteredData.length > 0 && totalPages > 1 && (
        <div className="rm-pagination" aria-label="Resource Master pagination">
          <span>Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filteredData.length)} of {filteredData.length}</span>
          <div className="rm-pagination-controls">
            <button type="button" className="rm-page-btn" onClick={() => setCurrentPage(safePage - 1)} disabled={safePage === 1}>Previous</button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button type="button" key={pageNumber} className={`rm-page-number ${pageNumber === safePage ? "active" : ""}`} onClick={() => setCurrentPage(pageNumber)}>{pageNumber}</button>
            ))}
            <button type="button" className="rm-page-btn" onClick={() => setCurrentPage(safePage + 1)} disabled={safePage === totalPages}>Next</button>
          </div>
        </div>
      )}

      {viewRow && (
        <div className="rm-modal-overlay" onClick={closeDetails}>
          <div className="rm-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rm-detail-header">
              <div className="rm-detail-identity">
                <div className="rm-detail-avatar">{(viewRow.employee_name || "?").charAt(0).toUpperCase()}</div>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ fontSize: 16, margin: 0, color: "#1F2937" }}>{viewRow.employee_name || "Resource"}</h3>
                  <div className="rm-detail-sub">
                    <span className="rm-id-badge">{viewRow.employee_id}</span>
                  </div>
                </div>
              </div>
              <div className="rm-detail-header-actions">
                {canEdit && (
                  <button className="rm-btn rm-btn-secondary" onClick={() => { closeDetails(); openEdit(viewRow); }}>Edit</button>
                )}
                <button className="rm-btn rm-btn-secondary" onClick={closeDetails}>Close</button>
              </div>
            </div>
            <div className="rm-detail-body">
              <div className="rm-detail-section-title">Overview</div>
              <div className="rm-detail-grid">
                {detailField("Employee No.", viewRow.employee_id)}
                {detailField("Employee Name", viewRow.employee_name)}
                {detailField("Working Hours / Month", viewRow.workingHoursPerMonth ?? viewRow.working_hours_per_month)}
                {detailField("Cost Per Hour", viewRow.costPerHour != null ? fmt(viewRow.costPerHour) : (viewRow.cost_per_hour != null ? fmt(viewRow.cost_per_hour) : ""))}
              </div>

              <div className="rm-detail-section-title">Cost &amp; Billing</div>
              <div className="rm-detail-grid">
                {detailField("Cost in Site", fmt(costRow(viewRow).csTotal))}
                {detailField("Cost in Site (Monthly)", viewRow.csMonthly != null ? fmt(viewRow.csMonthly) : "")}
                {detailField("UTS Office", fmt(costRow(viewRow).utsTotal))}
                {detailField("UTS Office (Monthly)", viewRow.utsMonthly != null ? fmt(viewRow.utsMonthly) : "")}
              </div>

              <div className="rm-detail-section-title">Project Allocation</div>
              {projectDetailsLoading ? (
                <div className="rm-project-status">Loading project assignments...</div>
              ) : projectDetailsError ? (
                <div className="rm-project-status rm-project-error">{projectDetailsError}</div>
              ) : projectDetails.length === 0 ? (
                <div className="rm-project-status">No project-resource records are assigned to this employee.</div>
              ) : (
                <div className="rm-detail-grid">
                  {projectDetails.map((project) => (
                    <div className="rm-detail-card" key={`alloc-${project.project_resource_id}`}>
                      <div className="rm-detail-label">{project.project_name || project.project_id}</div>
                      <div className="rm-detail-value">{project.allocation === null || project.allocation === undefined ? "—" : `${project.allocation}%`}</div>
                    </div>
                  ))}
                  <div className="rm-project-alloc-divider" />
                  <div className="rm-detail-card rm-project-alloc-total">
                    <div className="rm-detail-label">Projects Assigned</div>
                    <div className="rm-detail-value">{projectDetails.length}</div>
                  </div>
                  <div className="rm-detail-card rm-project-alloc-total">
                    <div className="rm-detail-label">Total Project Allocation</div>
                    <div className="rm-detail-value">{projectDetails.reduce((sum, p) => sum + (Number(p.allocation) || 0), 0)}%</div>
                  </div>
                </div>
              )}

              <div className="rm-detail-section-title">Customer Site Cost Breakdown</div>
              <div className="rm-detail-grid">
                {detailField("Labour", fmt(viewRow.cs_labour))}
                {detailField("Office", fmt(viewRow.cs_office))}
                {detailField("Laptop/Desktop", fmt(viewRow.cs_laptop_desktop))}
                {detailField("IT / Network Security", fmt(viewRow.cs_it_network_security))}
                {detailField("Program Management", fmt(viewRow.cs_program_management))}
              </div>

              <div className="rm-detail-section-title">UTS Office Cost Breakdown</div>
              <div className="rm-detail-grid">
                {detailField("Labour", fmt(viewRow.uts_labour))}
                {detailField("Office", fmt(viewRow.uts_office))}
                {detailField("Laptop/Desktop", fmt(viewRow.uts_laptop_desktop))}
                {detailField("IT / Network Security", fmt(viewRow.uts_it_network_security))}
                {detailField("Program Management", fmt(viewRow.uts_program_management))}
              </div>

              <div className="rm-detail-section-title">Project-Based Details</div>
              {projectDetailsLoading ? (
                <div className="rm-project-status">Loading project assignments...</div>
              ) : projectDetailsError ? (
                <div className="rm-project-status rm-project-error">{projectDetailsError}</div>
              ) : projectDetails.length === 0 ? (
                <div className="rm-project-status">No project-resource records are assigned to this employee.</div>
              ) : (
                <div className="rm-project-list">
                  {projectDetails.map((project, index) => (
                    <section className="rm-project-card" key={project.project_resource_id}>
                      <div className="rm-project-card-header">
                        <div>
                          <div className="rm-project-kicker">Project {index + 1}</div>
                          <h4>{project.project_name || project.project_id}</h4>
                        </div>
                        <span className="rm-project-id">{project.project_id}</span>
                      </div>
                      <div className="rm-detail-grid rm-project-grid">
                        {detailField("Project ID", project.project_id)}
                        {detailField("Project Name", project.project_name)}
                        {detailField("Customer", project.customer_name)}
                        {detailField("Resource Name", project.resource_name)}
                        {detailField("Allocation", project.allocation === null || project.allocation === undefined ? "" : `${project.allocation}%`)}
                        {detailField("Billable", project.billable)}
                        {detailField("Start Date", dateValue(project.billing_start_date))}
                        {detailField("End Date", dateValue(project.billing_end_date))}
                        {detailField("Project Execution At", project.project_execution_at)}
                        {detailField("Project Location", project.project_location)}
                        {detailField("PO / SOW / WO Ref.", project.po_sow_wo_ref)}
                        {detailField("PO Start Date", dateValue(project.po_start_date))}
                        {detailField("PO End Date", dateValue(project.po_end_date))}
                        {detailField("PO Role", project.po_role)}
                        {detailField("Estimated Revenue / Month", project.estimated_revenue_per_month === null || project.estimated_revenue_per_month === undefined ? "" : fmt(project.estimated_revenue_per_month))}
                        {detailField("Estimated Cost / Month", project.estimated_cost_per_month === null || project.estimated_cost_per_month === undefined ? "" : fmt(project.estimated_cost_per_month))}
                        {detailField("Project Type", project.project_type)}
                        {detailField("Currency", project.currency)}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="rm-modal-overlay" onClick={closeForm}>
          <div className="rm-modal" style={{ width: "820px" }} onClick={(e) => e.stopPropagation()}>
            <div className="rm-modal-header">
              <h3>{editingId ? "Edit Resource" : "Add Resource"}</h3>
              <button className="rm-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <div className="rm-modal-body">
              <div className="rm-form-grid" ref={empDropdownRef} style={{ position: "relative" }}>
                <div className="rm-form-group">
                  <label>Employee ID *</label>
                  <input
                    className="rm-input"
                    placeholder="Search employee..."
                    value={showEmpDropdown ? empSearch : formData.employeeId}
                    onChange={(e) => {
                      setFormData((p) => ({ ...p, employeeId: e.target.value, employeeName: "" }));
                      setEmpSearch(e.target.value);
                      setShowEmpDropdown(true);
                    }}
                    onFocus={() => { if (formData.employeeId && !formData.employeeName && !editingId) { setEmpSearch(formData.employeeId); setShowEmpDropdown(true); } }}
                    disabled={!!editingId}
                  />
                  {showEmpDropdown && employeeOptions.length > 0 && (
                    <div className="rm-emp-dropdown">
                      {employeeOptions.map((emp) => (
                        <div key={emp.employee_id} className="rm-emp-dropdown-item" onClick={() => selectEmployee(emp)}>
                          <strong>{emp.employee_id}</strong> &mdash; {emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`} <span style={{ color: "#94A3B8" }}>({emp.department || ""})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="rm-form-group">
                  <label>Employee Name *</label>
                  <input className="rm-input" name="employeeName" value={formData.employeeName} onChange={handleFormChange} disabled={!!editingId} />
                </div>
                <div className="rm-form-group">
                  <label>Working Hours / Month</label>
                  <input className="rm-input" type="number" min="1" step="1" name="workingHoursPerMonth" value={formData.workingHoursPerMonth} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Cost Per Hour</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="costPerHour" value={formData.costPerHour} onChange={handleFormChange} placeholder="0.00" />
                </div>
              </div>

              <div className="rm-form-divider"><span style={{ color: "#1E40AF" }}>Customer Site Costs</span></div>
              <div className="rm-form-grid">
                <div className="rm-form-group">
                  <label>Labour</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="csLabour" value={formData.csLabour} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Office</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="csOffice" value={formData.csOffice} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Laptop/Desktop (Fixed)</label>
                  <input className="rm-input" type="number" step="0.01" name="csLaptopDesktop" value="0" disabled style={{ background: "#F1F5F9", color: "#94A3B8" }} />
                </div>
                <div className="rm-form-group">
                  <label>IT Network/Security</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="csItNetworkSecurity" value={formData.csItNetworkSecurity} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Program Management</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="csProgramManagement" value={formData.csProgramManagement} onChange={handleFormChange} />
                </div>
              </div>
              <div className="rm-calc-row">
                <span>CS Total (per hour): <strong>{fmt(csFormTotal)}</strong></span>
                <span>CS Monthly (Total x {workingHours} hrs): <strong style={{ color: "#1E40AF" }}>{fmt(csFormMonthly)}</strong></span>
              </div>

              <div className="rm-form-divider"><span style={{ color: "#059669" }}>UTS Office Costs</span></div>
              <div className="rm-form-grid">
                <div className="rm-form-group">
                  <label>Labour</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="utsLabour" value={formData.utsLabour} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Office</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="utsOffice" value={formData.utsOffice} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Laptop/Desktop (Fixed)</label>
                  <input className="rm-input" type="number" step="0.01" name="utsLaptopDesktop" value="20" disabled style={{ background: "#F1F5F9", color: "#94A3B8" }} />
                </div>
                <div className="rm-form-group">
                  <label>IT Network/Security</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="utsItNetworkSecurity" value={formData.utsItNetworkSecurity} onChange={handleFormChange} />
                </div>
                <div className="rm-form-group">
                  <label>Program Management</label>
                  <input className="rm-input" type="number" min="0" step="0.01" name="utsProgramManagement" value={formData.utsProgramManagement} onChange={handleFormChange} />
                </div>
              </div>
              <div className="rm-calc-row">
                <span>UTS Total (per hour): <strong>{fmt(utsFormTotal)}</strong></span>
                <span>UTS Monthly (Total x {workingHours} hrs): <strong style={{ color: "#059669" }}>{fmt(utsFormMonthly)}</strong></span>
              </div>
            </div>
            <div className="rm-modal-footer">
              <button className="rm-btn rm-btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
              <button className="rm-btn rm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="rm-modal-overlay" onClick={() => { setShowDeleteModal(false); setDeletingId(null); setDeletingRow(null); }}>
          <div className="rm-modal rm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="rm-modal-header">
              <h3>Remove Resource</h3>
              <button className="rm-modal-close" onClick={() => { setShowDeleteModal(false); setDeletingId(null); setDeletingRow(null); }}>&times;</button>
            </div>
            <div className="rm-modal-body">
              <p style={{ margin: 0, color: "#475569", fontSize: "14px" }}>
                Are you sure you want to remove the resource record for <strong>{deletingRow?.employee_name || deletingRow?.employeeId || "this employee"}</strong>?
                This action cannot be undone.
              </p>
            </div>
            <div className="rm-modal-footer">
              <button className="rm-btn rm-btn-secondary" onClick={() => { setShowDeleteModal(false); setDeletingId(null); setDeletingRow(null); }} disabled={deleting}>Cancel</button>
              <button className="rm-btn rm-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
  .rm-list-card { width: 100%; background: #fff; border: 1px solid #D9E2EF; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 8px rgba(16,41,100,0.06); box-sizing: border-box; }
  .rm-table-wrapper { width: 100%; overflow: hidden; }
  .rm-table { width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 13px; }
  .rm-col-employee-id { width: 12%; }
  .rm-col-employee-name { width: 20%; }
  .rm-col-cost { width: 12%; }
  .rm-col-allocation { width: 8%; }
  .rm-col-actions { width: 20%; }
  .rm-table thead th { padding: 10px 12px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.35px; color: #64748B; background: #F8FAFC; border-bottom: 1px solid #D9E2EF; white-space: nowrap; vertical-align: middle; }
  .rm-table tbody td { padding: 11px 12px; border-bottom: 1px solid #E5E8EC; color: #1E293B; vertical-align: middle; }
  .rm-table tbody tr { transition: background 0.15s; }
  .rm-table tbody tr:nth-child(even) { background: #F8FAFC; }
  .rm-table tbody tr:hover { background: #EAF1F8; }
  .rm-num { text-align: right !important; }
  .rm-group-header { text-align: center !important; font-size: 11px !important; letter-spacing: 0.5px; border-bottom: 1px solid #D9E2EF !important; }
  .rm-group-cost { background: #EFF6FF !important; color: #1E40AF !important; }
  .rm-group-uts { background: #ECFDF5 !important; color: #059669 !important; }
  .rm-group-alloc { background: #F5F3FF !important; color: #6D28D9 !important; }
  .rm-sub-cost { background: #F0F5FF !important; font-size: 10px !important; }
  .rm-sub-uts { background: #F0FDF4 !important; font-size: 10px !important; }
  .rm-sub-alloc { background: #F8F6FF !important; color: #6D28D9 !important; text-align: center !important; font-size: 10px !important; white-space: nowrap; }
  .rm-actions-header { text-align: center !important; }
  .rm-footer-row td { background: #F1F5F9; border-top: 2px solid #D9E2EF; padding: 10px 12px; }
  .rm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 24px; color: #94A3B8; }
  .rm-pagination { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; color: #64748B; font-size: 12px; }
  .rm-pagination-controls { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; justify-content: flex-end; }
  .rm-page-btn, .rm-page-number { min-width: 30px; height: 30px; padding: 0 9px; border: 1px solid #D9E2EF; border-radius: 6px; background: #fff; color: #475569; font: 600 12px 'Inter', sans-serif; cursor: pointer; }
  .rm-page-btn:hover:not(:disabled), .rm-page-number:hover, .rm-page-number.active { background: #EFF6FF; border-color: #1E40AF; color: #1E40AF; }
  .rm-page-number.active { font-weight: 700; }
  .rm-page-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .rm-action-btn { min-width: 58px; height: 30px; background: #fff; border: 1px solid #D9E2EF; border-radius: 6px; padding: 0 8px; cursor: pointer; font: 600 12px 'Inter', sans-serif; transition: all 0.15s; }
  .rm-action-view { color: #1E40AF; }
  .rm-action-view:hover { background: #EFF6FF; border-color: #1E40AF; }
  .rm-action-edit { color: #D97706; }
  .rm-action-edit:hover { background: #FFFBEB; border-color: #D97706; }
  .rm-action-danger { color: #DC2626; }
  .rm-action-danger:hover { background: #FEF2F2; color: #DC2626; border-color: #FECACA; }
  .rm-actions-cell { white-space: nowrap; text-align: center !important; }
  .rm-action-set { display: flex; align-items: center; justify-content: center; gap: 6px; flex-wrap: wrap; }
  .rm-name-cell { display: flex; align-items: center; gap: 8px; font-weight: 600; color: #0F172A; }
  .rm-avatar { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: #1E40AF; color: #fff; font-size: 12px; font-weight: 700; flex-shrink: 0; }
  .rm-id-badge { display: inline-block; padding: 2px 8px; border-radius: 6px; background: #EFF6FF; color: #1E40AF; border: 1px solid #BFDBFE; font-family: "Consolas", "Menlo", monospace; font-size: 12px; font-weight: 700; white-space: nowrap; }
  .rm-cost-value { font-weight: 700; color: #1E40AF !important; }
  .rm-employee-id { white-space: nowrap; }
  .rm-alloc-cell { text-align: center !important; font-weight: 700; font-variant-numeric: tabular-nums; color: #475569 !important; white-space: nowrap; }
  .rm-alloc-allocated { color: #1E40AF !important; }
  .rm-input, .rm-select { width: 100%; padding: 8px 12px; border: 1px solid #D9E2EF; border-radius: 8px; font-size: 13px; font-family: 'Inter', sans-serif; color: #1E293B; background: #fff; box-sizing: border-box; outline: none; transition: border-color 0.15s; }
  .rm-input:focus, .rm-select:focus { border-color: #1E40AF; box-shadow: 0 0 0 2px rgba(30,64,175,0.1); }
  .rm-input:disabled, .rm-select:disabled { background: #F8FAFC; color: #94A3B8; cursor: not-allowed; }
  .rm-search { padding-left: 30px; }
  .rm-btn { padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; font-family: 'Inter', sans-serif; cursor: pointer; transition: all 0.15s; border: none; }
  .rm-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .rm-btn-primary { background: linear-gradient(135deg, #1E40AF, #1e3a8a); color: #fff; }
  .rm-btn-primary:hover:not(:disabled) { box-shadow: 0 4px 12px rgba(30,64,175,0.3); }
  .rm-btn-secondary { background: #fff; color: #475569; border: 1px solid #D9E2EF; }
  .rm-btn-secondary:hover:not(:disabled) { background: #F8FAFC; }
  .rm-btn-danger { background: #DC2626; color: #fff; }
  .rm-btn-danger:hover:not(:disabled) { background: #B91C1C; }
  .rm-modal-overlay { position: fixed; inset: 0; padding: 20px; background: rgba(15,23,42,0.52); display: flex; align-items: center; justify-content: center; z-index: 1000; box-sizing: border-box; }
  .rm-modal { background: #fff; border-radius: 14px; width: 680px; max-width: 95vw; max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 60px rgba(15,23,42,0.25); }
  .rm-modal-sm { width: 420px; }
  .rm-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 24px; border-bottom: 1px solid #E2E8F0; }
  .rm-modal-header h3 { margin: 0; font-size: 18px; font-weight: 700; color: #1E293B; }
  .rm-modal-close { background: none; border: none; font-size: 22px; color: #94A3B8; cursor: pointer; padding: 0 4px; line-height: 1; }
  .rm-modal-close:hover { color: #475569; }
  .rm-modal-body { padding: 20px 24px; }
  .rm-modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 24px; border-top: 1px solid #E2E8F0; }
  .rm-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; }
  .rm-form-group { display: flex; flex-direction: column; gap: 4px; }
  .rm-form-group label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: #64748B; }
  .rm-form-divider { margin: 18px 0 14px; padding-bottom: 0; border-bottom: 1px solid #E2E8F0; }
  .rm-form-divider span { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
  .rm-emp-dropdown { position: absolute; top: 100%; left: 0; right: 0; background: #fff; border: 1px solid #D9E2EF; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); max-height: 200px; overflow-y: auto; z-index: 10; margin-top: 4px; }
  .rm-emp-dropdown-item { padding: 8px 12px; font-size: 13px; cursor: pointer; border-bottom: 1px solid #F1F5F9; }
  .rm-emp-dropdown-item:last-child { border-bottom: none; }
  .rm-emp-dropdown-item:hover { background: #F0F5FF; }
  .rm-calc-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #F8FAFC; border-radius: 6px; margin-top: 8px; font-size: 13px; color: #475569; }
  .rm-detail-modal { background: #fff; border-radius: 12px; max-width: 980px; width: 100%; max-height: calc(100vh - 40px); overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(15,23,42,0.25); }
  .rm-detail-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; border-bottom: 1px solid #D9E2EF; background: #F8FAFC; flex-shrink: 0; }
  .rm-detail-identity { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .rm-detail-avatar { width: 44px; height: 44px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: #1E40AF; color: #fff; font-size: 18px; font-weight: 700; flex-shrink: 0; }
  .rm-detail-sub { display: flex; align-items: center; gap: 8px; margin-top: 6px; flex-wrap: wrap; }
  .rm-detail-header-actions { display: flex; gap: 8px; }
  .rm-detail-body { padding: 18px 20px 22px; overflow-y: auto; }
  .rm-detail-section-title { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.06em; margin: 18px 0 8px; }
  .rm-detail-section-title:first-child { margin-top: 0; }
  .rm-detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 8px; }
  .rm-detail-card { min-width: 0; padding: 9px 11px; border: 1px solid #E2E8F0; border-radius: 7px; background: #FAFCFE; }
  .rm-detail-label { font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 4px; }
  .rm-detail-value { font-size: 13px; font-weight: 600; color: #1F2937; word-break: break-word; }
  .rm-project-list { display: grid; gap: 12px; }
  .rm-project-card { border: 1px solid #D9E2EF; border-radius: 10px; overflow: hidden; background: #fff; }
  .rm-project-card-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 14px; background: #F5F8FC; border-bottom: 1px solid #E2E8F0; }
  .rm-project-card-header h4 { margin: 2px 0 0; color: #1E293B; font-size: 14px; line-height: 1.3; }
  .rm-project-kicker { color: #64748B; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
  .rm-project-id { flex-shrink: 0; padding: 3px 7px; color: #1E40AF; background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 5px; font: 700 11px "Consolas", "Menlo", monospace; }
  .rm-project-grid { padding: 12px; }
  .rm-project-status { padding: 16px; color: #64748B; background: #F8FAFC; border: 1px dashed #CBD5E1; border-radius: 8px; font-size: 13px; }
  .rm-project-error { color: #B91C1C; border-color: #FCA5A5; background: #FEF2F2; }
  .rm-project-alloc-divider { grid-column: 1 / -1; height: 0; border-top: 1px dashed #CBD5E1; margin: 2px 0; }
  .rm-project-alloc-total { background: #EFF6FF; border-color: #BFDBFE; }
  @media (max-width: 768px) {
    .rm-modal-overlay { padding: 10px; }
    .rm-detail-header { align-items: flex-start; gap: 10px; }
    .rm-detail-header-actions { flex-shrink: 0; }
    .rm-form-grid { grid-template-columns: 1fr; }
    .rm-detail-grid { grid-template-columns: 1fr; }
    .rm-project-card-header { align-items: flex-start; flex-direction: column; }
    .rm-table { font-size: 12px; }
    .rm-table thead th, .rm-table tbody td { padding: 9px 7px; }
    .rm-table thead th { font-size: 9px; white-space: normal; }
    .rm-name-cell { gap: 5px; }
    .rm-avatar { width: 24px; height: 24px; font-size: 11px; }
    .rm-id-badge { padding: 2px 4px; font-size: 10px; }
    .rm-action-btn { min-width: 52px; height: 28px; font-size: 11px; }
    .rm-action-set { gap: 4px; }
    .rm-pagination { align-items: flex-start; flex-direction: column; }
    .rm-pagination-controls { justify-content: flex-start; }
  }
`;
